import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { ChangeEvent, KeyboardEvent, useCallback, useState } from "react";
import { bolt } from "utils/metricsLogger";

import {
  commitTypedText,
  hasIncompleteTerm,
  serializeOmnisearch,
  splitOmnisearch,
} from "./omnisearchPills";
import { EMPTY_KEY_ALIASES, isMultiValueKey } from "./pillFilterKeys";

type UseOmnisearchTextInputParams = {
  omnisearch: string;
  /** When true the input holds only free text and filters live in pills. */
  showPills: boolean;
  /** Used to decide whether a typed term joins or replaces an existing filter. */
  columnFilterKeys?: FilterKey[];
  /** Maps a filter key to the key it serializes as, for grids that rename one. */
  keyAliases?: Readonly<Record<string, string>>;
  setOmnisearch: (newVal: string) => void;
  setOmnisearchImmediate?: (newVal: string) => void;
};

/** What the box shows: only the free text when filters live in pills. */
function inputValueFor(omnisearch: string, showPills: boolean): string {
  return showPills ? splitOmnisearch(omnisearch).freeText : omnisearch;
}

/**
 * Owns the search input's value and the query updates it drives.
 *
 * The input keeps its own state so a parent re-render can't block typing. With
 * pills on it holds only the free-text part of the query, and a complete
 * `key:value` term the user types is folded into the pills instead.
 */
export function useOmnisearchTextInput({
  omnisearch,
  showPills,
  columnFilterKeys,
  keyAliases = EMPTY_KEY_ALIASES,
  setOmnisearch,
  setOmnisearchImmediate,
}: UseOmnisearchTextInputParams) {
  const isMultiValue = useCallback(
    (key: string) => isMultiValueKey(key, columnFilterKeys ?? [], keyAliases),
    [columnFilterKeys, keyAliases]
  );

  const [value, setValue] = useState(() => inputValueFor(omnisearch, showPills));

  // The query this hook last handed out, alongside the prop it was built from.
  //
  // `query` is what the next edit builds on: typing is debounced, so the prop
  // lags behind by up to the debounce interval, and folding a term from the
  // stale prop would drop whatever hasn't landed yet. `prop` tells this hook's
  // own echo apart from a change made somewhere else (a URL edit, applied
  // filters, a removed pill) — only the latter may overwrite what is typed.
  const [pushed, setPushed] = useState({ prop: omnisearch, query: omnisearch, showPills });

  if (pushed.prop !== omnisearch || pushed.showPills !== showPills) {
    const isOwnEcho = pushed.showPills === showPills && pushed.query === omnisearch;
    setPushed({ prop: omnisearch, query: omnisearch, showPills });
    if (!isOwnEcho) {
      setValue(inputValueFor(omnisearch, showPills));
    }
  }

  const currentQuery = pushed.prop === omnisearch ? pushed.query : omnisearch;

  const pushQuery = useCallback(
    (next: string, immediate: boolean) => {
      setPushed({ prop: omnisearch, query: next, showPills });
      if (immediate && setOmnisearchImmediate) {
        setOmnisearchImmediate(next);
      } else {
        setOmnisearch(next);
      }
    },
    [omnisearch, setOmnisearch, setOmnisearchImmediate, showPills]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const typedText = event.target.value;
      setValue(typedText);

      if (!showPills) {
        pushQuery(typedText, false);
        return;
      }

      // Wait for a half-typed term to finish, so the query isn't rewritten and
      // a pill doesn't flash for `state:C` on the way to `state:CA`.
      if (hasIncompleteTerm(typedText)) {
        return;
      }

      const committed = commitTypedText(currentQuery, typedText, isMultiValue);
      // Only take the box back when a term became a pill. Otherwise leave what
      // was typed exactly as typed, spaces and all.
      if (committed.folded) {
        setValue(committed.freeText);
      }
      // A folded term is a filter change, not a search-as-you-type keystroke, so
      // it lands right away — the pill appears with the space that made it.
      pushQuery(committed.omnisearch, committed.folded);
    },
    [currentQuery, isMultiValue, pushQuery, showPills]
  );

  // Enter commits a term typed without a trailing space.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!showPills || event.key !== "Enter") {
        return;
      }
      const committed = commitTypedText(currentQuery, value, isMultiValue);
      if (committed.folded) {
        setValue(committed.freeText);
      }
      pushQuery(committed.omnisearch, true);
    },
    [currentQuery, isMultiValue, pushQuery, showPills, value]
  );

  const handleClear = useCallback(() => {
    bolt.startInteraction("omnisearch.clear");
    setValue("");
    // With pills the input holds only the free-text search, so clearing it
    // leaves the applied filters alone — the pill row owns those.
    pushQuery(showPills ? serializeOmnisearch(splitOmnisearch(currentQuery).pairs, "") : "", true);
  }, [currentQuery, pushQuery, showPills]);

  return { value, handleChange, handleKeyDown, handleClear };
}
